import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
    Card,
    CardBody,
    Button,
    Col,
    Container,
    Row
} from "reactstrap";

import PlantSelect from "../PlantList/PlantSelect";
import PlantPreviewCard from "../PlantList/PlantPreviewCard";
import { getPlantData, getClimateData } from "../../store/design/actions";
import { getProfile } from "../../store/auth/profile/actions";
const Spline = require("cubic-spline");

/* =====================================================
   Crop Planner
===================================================== */
const CropPlanner = ({
                         plantData,
                         plantMap,
                         climateData,
                         profile,
                         onGetPlantData,
                         onGetProfile,
                         onGetClimateData,
                         areas,
                         currYear,
                         onAddRotationToArea
                     }) => {

    /* -----------------------------
       Step control
    ------------------------------ */
    const [step, setStep] = useState(1);
    const username = JSON.parse(localStorage.getItem("user")).user;

    const Arrow = () => (
        <div
            className="d-flex justify-content-center align-items-center h-100"
            style={{ fontSize: "2.75rem", color: "#adb5bd" }}
        >
            <i className="bx bx-right-arrow-alt" />
        </div>
    );

    /* -----------------------------
       Plant selection
    ------------------------------ */
    const [plants, setPlants] = useState([]);
    const [plantOptions, setPlantOptions] = useState([]);
    const [errors, setErrors] = useState({});
    const [rotations, setRotations] = useState([]);

    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

    /* -----------------------------
       Load data
    ------------------------------ */
    useEffect(() => {
        onGetProfile(username);
        onGetPlantData();
    }, []);

    useEffect(() => {
        if (profile?.address?.zipcode) {
            onGetClimateData(profile.address.zipcode);
        }
    }, [profile?.address?.zipcode]);

    useEffect(() => {
        setPlantOptions(
            plantData
                .filter(p => p.season !== "perennial")
                .map(p => ({ key: p.name, label: p.name }))
        );
    }, [plantData]);

    /* -----------------------------
       Helpers
    ------------------------------ */
    function getWeatherType(plant) {
        if (!plant?.minimumTemp || !plant?.maximumTemp) return null;
        const min = parseFloat(plant.minimumTemp);
        const max = parseFloat(plant.maximumTemp);
        return (min + max) / 2 >= 70 ? "warm" : "cool";
    }

    /* -----------------------------
       Plant CRUD
    ------------------------------ */
    function addPlant() {
        setPlants([...plants, { species: "" }]);
    }

    function removePlant(index) {
        setPlants(plants.filter((_, i) => i !== index));
    }

    function updateSpecies(index, val) {
        const species = typeof val === "string" ? val : val?.key;
        if (!species || !plantMap[species]) return;

        const updated = [...plants];
        updated[index] = { species, ...plantMap[species] };
        setPlants(updated);
    }

    /* -----------------------------
       Validation
    ------------------------------ */
    function validatePlants() {
        let errs = {};
        plants.forEach((p, i) => {
            if (!p.species) errs[i] = "Please select a plant";
        });
        setErrors(errs);
        return Object.keys(errs).length === 0;
    }

    /* -----------------------------
       Date math
    ------------------------------ */
    function calcExactDay(keyMonth, min, max, climateInfo, baseYear) {
        const xs = [0, 30, 60];
        const prevMonth = (keyMonth + 11) % 12;
        const nextMonth = (keyMonth + 1) % 12;

        const ymins = [
            parseFloat(climateInfo[months[prevMonth] + "Min"]),
            parseFloat(climateInfo[months[keyMonth] + "Min"]),
            parseFloat(climateInfo[months[nextMonth] + "Min"])
        ];

        const ymaxs = [
            parseFloat(climateInfo[months[prevMonth] + "Max"]),
            parseFloat(climateInfo[months[keyMonth] + "Max"]),
            parseFloat(climateInfo[months[nextMonth] + "Max"])
        ];

        const minSpline = new Spline(xs, ymins);
        const maxSpline = new Spline(xs, ymaxs);

        let keyDate = null;
        for (let i = 0; i <= 60; i++) {
            if (minSpline.at(i) >= min && maxSpline.at(i) <= max) {
                keyDate = i;
                break;
            }
        }
        if (keyDate == null) return null;

        const midPrev = new Date(baseYear, prevMonth, 15);
        const d = new Date(midPrev);
        d.setDate(d.getDate() + keyDate);
        return d;
    }

    const SEASON_MONTHS = {
        Spring: [1,2,3,4],   // Feb–May
        Summer: [5,6,7,8],   // Jun–Sep
        Fall:   [9,10,11]    // Oct–Dec
    };

    function recalcPlantDatesForSeason(plant, season) {
        if (!plant || !climateData) return plant;

        let climateInfo = climateData;
        if (profile?.climates?.length) {
            const selected = profile.climates.find(c => c.selected);
            if (selected) climateInfo = selected;
        }

        const monthsToTry = SEASON_MONTHS[season];
        if (!monthsToTry) return plant;

        const min = parseFloat(plant.minimumTemp);
        const max = parseFloat(plant.maximumTemp);
        const baseYear = new Date().getFullYear();

        let startDate = null;
        for (let m of monthsToTry) {
            startDate = calcExactDay(m, min, max, climateInfo, baseYear);
            if (startDate) break;
        }
        if (!startDate) return plant;

        const daysToTransplant = parseInt(plant.daysToTransplant || 0);
        const germination = parseInt(plant.germinationTime || 0);
        const daysToHarvest = parseInt(plant.daysToHarvest || 0);

        const seedDate = new Date(startDate);
        seedDate.setDate(seedDate.getDate() - daysToTransplant - germination);

        const transplantDate = new Date(startDate);
        const harvestDate = new Date(startDate);
        harvestDate.setDate(harvestDate.getDate() + daysToHarvest);

        return {
            ...plant,
            season_rotation: season,
            seedStartDate: seedDate,
            transplantDate,
            harvestDate
        };
    }

    /* -----------------------------
       Crop rotation logic
    ------------------------------ */
    function generateCropRotations(plants) {
        const remaining = [...plants];
        const rotations = [];

        const nutrientRank = {
            heavy_giver: 0,
            light_feeder: 1,
            heavy_feeder: 2
        };

        function pickBest(candidates, prevPlant, families) {
            let best = null;
            let bestScore = -Infinity;

            for (const p of candidates) {
                let score = 0;
                if (prevPlant) {
                    const prev = nutrientRank[prevPlant.nutrient_role] ?? 1;
                    const curr = nutrientRank[p.nutrient_role] ?? 1;
                    if (curr >= prev) score += 5;
                } else score += 2;

                if (p.family && !families.has(p.family)) score += 1;
                if (p.daysToHarvest) score += Math.max(0, 120 - p.daysToHarvest) / 100;

                if (score > bestScore) {
                    best = p;
                    bestScore = score;
                }
            }
            return best;
        }

        while (remaining.length) {
            const rotation = { spring: null, summer: null, fall: null };
            const families = new Set();

            const cool = remaining.filter(p => getWeatherType(p) === "cool");
            const warm = remaining.filter(p => getWeatherType(p) === "warm");

            const spring = pickBest(cool, null, families);
            if (spring) { rotation.spring = spring; families.add(spring.family); }

            const summer = pickBest(warm, spring, families);
            if (summer) { rotation.summer = summer; families.add(summer.family); }

            const fall = pickBest(cool.filter(p => p !== spring), summer || spring, families);
            if (fall) { rotation.fall = fall; families.add(fall.family); }

            if (!spring && !summer && !fall) break;

            [spring, summer, fall].filter(Boolean).forEach(p => {
                const idx = remaining.findIndex(r => r.species === p.species);
                if (idx >= 0) remaining.splice(idx, 1);
            });

            rotations.push(rotation);
        }
        return rotations;
    }

    /* -----------------------------
       Step transitions
    ------------------------------ */
    function goNext() {
        if (step === 1 && validatePlants()) {
            const raw = generateCropRotations(plants);
            setRotations(raw.map(r => ({
                spring: r.spring ? recalcPlantDatesForSeason(r.spring, "Spring") : null,
                summer: r.summer ? recalcPlantDatesForSeason(r.summer, "Summer") : null,
                fall: r.fall ? recalcPlantDatesForSeason(r.fall, "Fall") : null
            })));
            setStep(2);
        }
    }

    const [selectedAreaId, setSelectedAreaId] = useState("");


    function goBack() {
        setStep(step - 1);
    }

    function handleAddRotation(rotation) {
        const plantsToAdd = [];

        ["spring", "summer", "fall"].forEach(season => {
            if (rotation[season]) {
                plantsToAdd.push(
                    buildPlantForArea(rotation[season], profile)
                );
            }
        });

        onAddRotationToArea(selectedAreaId, plantsToAdd);
    }


    /* -----------------------------
       Render
    ------------------------------ */
    return (
        <div className="page-content">
            <Container fluid>
                <Card>
                    <CardBody>
                        <h4>Crop Planner</h4>

                        {step === 1 && (
                            <>
                                <Input
                                    type="select"
                                    value={selectedAreaId}
                                    onChange={e => setSelectedAreaId(e.target.value)}
                                >
                                    <option value="">Select Area</option>
                                    {areas
                                        .filter(a => a.year === currYear)
                                        .map(a => (
                                            <option key={a.id} value={a.id}>
                                                {a.name}
                                            </option>
                                        ))}
                                </Input>

                                <Row className="g-3">
                                    {plants.map((plant, idx) => (
                                        <Col key={idx} xs={12} sm={6} md={4} lg={3}>
                                            {!plant.species ? (
                                                <Card outline className="h-100" style={{ border: "2px dashed #dee2e6" }}>
                                                    <CardBody>
                                                        <PlantSelect
                                                            plantOptions={plantOptions}
                                                            value={null}
                                                            onChange={(val) => updateSpecies(idx, val)}
                                                        />
                                                    </CardBody>
                                                </Card>
                                            ) : (
                                                <PlantPreviewCard plant={plant} unitmeasurement={profile.unitmeasurement} />
                                            )}
                                        </Col>
                                    ))}
                                </Row>

                                <div className="mt-4 d-flex justify-content-between">
                                    <Button color="secondary" onClick={addPlant}>+ Add another crop</Button>
                                    <Button color="primary" onClick={goNext} disabled={!plants.length}>Continue</Button>
                                </div>
                            </>
                        )}

                        {step === 2 && (
                            <>
                                <h5>Crop Rotations</h5>

                                {rotations.map((rotation, idx) => (
                                    <Row key={idx} className="align-items-start mb-4">
                                        <Col md={3}>{rotation.spring && <PlantPreviewCard plant={rotation.spring} unitmeasurement={profile.unitmeasurement} mode="rotation" />}</Col>
                                        <Col md={1}><Arrow /></Col>
                                        <Col md={3}>{rotation.summer && <PlantPreviewCard plant={rotation.summer} unitmeasurement={profile.unitmeasurement} mode="rotation" />}</Col>
                                        <Col md={1}><Arrow /></Col>
                                        <Col md={3}>{rotation.fall && <PlantPreviewCard plant={rotation.fall} unitmeasurement={profile.unitmeasurement} mode="rotation" />}</Col>
                                        <Col md={1} className="text-center align-self-center">
                                            <Button outline color="primary" onClick={() => handleAddRotationToArea(rotation)}>
                                                Add To Area
                                            </Button>
                                        </Col>
                                    </Row>
                                ))}

                                <Button onClick={goBack}>Back</Button>
                            </>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

/* -----------------------------
   Redux
------------------------------ */
const mapStateToProps = (state) => {
    const sorted = [...state.designer.plants].sort((a, b) =>
        a.name.localeCompare(b.name)
    );
    return {
        plantData: sorted,
        plantMap: sorted.reduce((m, p) => { m[p.name] = p; return m; }, {}),
        climateData: state.designer.temps,
        profile: state.Profile.user
    };
};

const mapDispatchToProps = (dispatch) => ({
    onGetPlantData: () => dispatch(getPlantData()),
    onGetProfile: (u) => dispatch(getProfile(u)),
    onGetClimateData: (z) => dispatch(getClimateData(z))
});

export default connect(mapStateToProps, mapDispatchToProps)(CropPlanner);
